import type { QueryClient, QueryKey } from '@tanstack/react-query';

// Factories for the two optimistic-mutation shapes that recur across the data
// layer. Each returns the `onMutate` / `onError` / (`onSuccess` | `onSettled`)
// handlers to spread into `useMutation`; `meta` and `mutationFn` stay inline at
// the call site. The classic cancel → snapshot → patch → rollback dance lives
// here once instead of being copy-pasted per mutation.

/**
 * Optimistically patch a single cached object, roll back on error, and write
 * the server's response on success. The cache key is static.
 */
export function optimisticObjectPatch<TData, TVars>(
  qc: QueryClient,
  queryKey: QueryKey,
  applyPatch: (previous: TData, vars: TVars) => TData,
) {
  return {
    onMutate: async (vars: TVars): Promise<{ previous: TData | undefined }> => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<TData>(queryKey);
      if (previous) {
        qc.setQueryData<TData>(queryKey, applyPatch(previous, vars));
      }
      return { previous };
    },
    onError: (
      _err: unknown,
      _vars: TVars,
      ctx: { previous: TData | undefined } | undefined,
    ) => {
      if (ctx?.previous) {
        qc.setQueryData(queryKey, ctx.previous);
      }
    },
    onSuccess: (next: TData) => {
      qc.setQueryData(queryKey, next);
    },
  };
}

/**
 * Optimistically update a cached list (filter-remove or item-map), roll back on
 * error, and invalidate on settle. `keyFor` derives the cache key from the
 * mutation variables, so it works whether the key is static or parameterised
 * (e.g. by `workspaceId`). The resolved key is stashed in context so rollback
 * targets the exact list that was patched.
 */
export function optimisticListUpdate<TItem, TVars>(
  qc: QueryClient,
  keyFor: (vars: TVars) => QueryKey,
  updater: (list: TItem[], vars: TVars) => TItem[],
) {
  return {
    onMutate: async (
      vars: TVars,
    ): Promise<{ previous: TItem[] | undefined; queryKey: QueryKey }> => {
      const queryKey = keyFor(vars);
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<TItem[]>(queryKey);
      if (previous) {
        qc.setQueryData<TItem[]>(queryKey, updater(previous, vars));
      }
      return { previous, queryKey };
    },
    onError: (
      _err: unknown,
      _vars: TVars,
      ctx: { previous: TItem[] | undefined; queryKey: QueryKey } | undefined,
    ) => {
      if (ctx?.previous) {
        qc.setQueryData(ctx.queryKey, ctx.previous);
      }
    },
    onSettled: (_data: unknown, _err: unknown, vars: TVars) => {
      void qc.invalidateQueries({ queryKey: keyFor(vars) });
    },
  };
}
